/**
 * Image optimization utilities for client-side processing
 */
import { getErrorMessage } from '@voley/shared-utils';

// Default timeout for image loading operations (10 seconds)
const IMAGE_LOAD_TIMEOUT_MS = 10000;

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface OptimizeImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'webp' | 'png';
  cropArea?: CropArea;
  /** Timeout in milliseconds for image loading (default: 10000) */
  timeoutMs?: number;
}

/**
 * Get image dimensions from a file with timeout protection
 */
export const getImageDimensions = (file: File, timeoutMs: number = IMAGE_LOAD_TIMEOUT_MS): Promise<ImageDimensions> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      if (!settled) {
        settled = true;
        // Clean up Image object to prevent memory leaks
        img.onload = null;
        img.onerror = null;
        img.src = '';
        URL.revokeObjectURL(url);
      }
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Image load timeout after ${timeoutMs}ms`));
    }, timeoutMs);

    img.onload = () => {
      clearTimeout(timeoutId);
      const dimensions = { width: img.width, height: img.height };
      cleanup();
      resolve(dimensions);
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      cleanup();
      reject(new Error('Failed to load image - file may be corrupted or invalid'));
    };

    img.src = url;
  });
};

/**
 * Resize and optimize an image file with timeout protection
 */
export const optimizeImage = (
  file: File,
  options: OptimizeImageOptions = {}
): Promise<File> => {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.9,
    format = 'jpeg',
    cropArea,
    timeoutMs = IMAGE_LOAD_TIMEOUT_MS
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    let settled = false;

    const cleanup = () => {
      if (!settled) {
        settled = true;
        // Clean up Image object to prevent memory leaks
        img.onload = null;
        img.onerror = null;
        img.src = '';
        URL.revokeObjectURL(url);
      }
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error(`Image optimization timeout after ${timeoutMs}ms - image may be too large or corrupted`));
    }, timeoutMs);

    img.onload = () => {
      clearTimeout(timeoutId);

      try {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          cleanup();
          reject(new Error('Failed to get canvas context - browser may not support canvas'));
          return;
        }

        let sourceX = 0;
        let sourceY = 0;
        let sourceWidth = img.width;
        let sourceHeight = img.height;

        // Apply crop if specified
        if (cropArea) {
          sourceX = cropArea.x;
          sourceY = cropArea.y;
          sourceWidth = cropArea.width;
          sourceHeight = cropArea.height;
        }

        // Calculate new dimensions maintaining aspect ratio
        let newWidth = sourceWidth;
        let newHeight = sourceHeight;

        if (sourceWidth > maxWidth || sourceHeight > maxHeight) {
          const aspectRatio = sourceWidth / sourceHeight;

          if (sourceWidth > sourceHeight) {
            newWidth = Math.min(sourceWidth, maxWidth);
            newHeight = newWidth / aspectRatio;
          } else {
            newHeight = Math.min(sourceHeight, maxHeight);
            newWidth = newHeight * aspectRatio;
          }
        }

        // Set canvas dimensions
        canvas.width = newWidth;
        canvas.height = newHeight;

        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw the image
        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          0,
          0,
          newWidth,
          newHeight
        );

        // Convert to blob with timeout for toBlob operation
        const blobTimeoutId = setTimeout(() => {
          cleanup();
          reject(new Error('Image blob creation timeout - image may be too large'));
        }, 5000); // 5 second timeout for blob creation

        canvas.toBlob(
          (blob) => {
            clearTimeout(blobTimeoutId);
            cleanup();

            if (blob && blob.size > 0) {
              // Create new file from blob with proper extension handling
              const hasExtension = file.name.includes('.') && file.name.lastIndexOf('.') > 0;
              const newFileName = hasExtension
                ? file.name.replace(/\.[^/.]+$/, `.${format}`)
                : `${file.name}.${format}`;
              const optimizedFile = new File(
                [blob],
                newFileName,
                { type: `image/${format}` }
              );

              // Final validation
              if (optimizedFile.size === 0) {
                reject(new Error('Created file is empty - image processing failed'));
                return;
              }

              // Optimization succeeded (debug detail omitted for production compliance)

              resolve(optimizedFile);
            } else {
              reject(new Error('Failed to create image blob - browser may have insufficient memory'));
            }
          },
          `image/${format}`,
          quality
        );
      } catch (error) {
        cleanup();
        reject(new Error(`Image processing failed: ${getErrorMessage(error)}`));
      }
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      cleanup();
      reject(new Error('Failed to load image - file may be corrupted or invalid'));
    };

    img.src = url;
  });
};

/**
 * Convert file size to human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Check if file size exceeds limit
 */
export const isFileSizeValid = (file: File, maxSizeMB: number = 5): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Generate a preview URL for a file
 */
export const generatePreviewUrl = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Clean up a preview URL
 */
export const revokePreviewUrl = (url: string): void => {
  URL.revokeObjectURL(url);
};